import { AccessLevel, Difficulty, Placement, Prisma } from '@prisma/client';

import { ParseError } from '../../errors/parse-error';
import * as guards from './guards';
import * as AppTypes from './app-types';
import * as UserTypes from './user-types';
import { SearchParams } from './search-params';
import { EncryptedVkData } from './session-types';
import { OpenGraph } from 'next/dist/lib/metadata/types/opengraph-types';
import { SubscriptionForSchema } from './metadata-types';
import { BranchForSubscription, BranchWithParsedCases } from './branch-types';

export const parseString = (value: unknown): string => {
  if (guards.isString(value)) return value;
  throw new ParseError('Объект не соответствует типу string');
};

export const parseNumber = (value: unknown): number => {
  if (guards.isNumber(value)) return value;
  throw new ParseError('Объект не соответствует типу number');
};

export const parseBoolean = (value: unknown): boolean => {
  if (guards.isBoolean(value)) return value;
  throw new ParseError('Объект не соответствует типу boolean');
};

export function parseStringArray(values: unknown): string[] {
  if (!Array.isArray(values))
    throw new ParseError('Объект не соответствует типу string[]');

  return values
    .map((item: unknown) => {
      if (guards.isString(item)) {
        return item;
      }

      return null;
    })
    .filter((item): item is string => item !== null); // Отфильтровываем некорректные объекты
}

export function parseDifficulty(value: unknown): Difficulty {
  if (guards.isDifficulty(value)) return value;
  throw new ParseError('Объект не соответствует типу Difficulty');
}

export function parsePlacement(value: unknown): Placement {
  if (guards.isPlacement(value)) return value;
  throw new ParseError('Объект не соответствует типу Placement');
}

export function parseDate(value: unknown): Date {
  if (guards.isValidDate(value)) return value;
  throw new ParseError('Объект не соответствует типу Date');
}
export function parseNullableDate(value: unknown): Date | null {
  if (value === null) return null;
  if (guards.isValidDate(value)) return value;
  throw new ParseError('Объект не соответствует типу Date | null');
}

export function parseAccessLevel(value: unknown): AccessLevel {
  if (guards.isAccessLevel(value)) return value;
  throw new ParseError('Объект не соответствует типу AccessLevel');
}

export function parseUserPersonalData(obj: unknown): AppTypes.UserPersonalData {
  if (guards.isUserPersonalData(obj)) return obj;
  throw new ParseError('Объект не соответствует типу UserPersonalData');
}

export function parseUserPreferences(obj: unknown): AppTypes.UserPreferences {
  if (guards.isUserPreferences(obj)) return obj;
  throw new ParseError('Объект не соответствует типу UserPreferences');
}

export function parseUserClient(obj: unknown): AppTypes.UserClient {
  if (guards.isUserClient(obj)) return obj;
  throw new ParseError('Объект не соответствует типу UserClient');
}

export function parseSearchParams(obj: unknown): SearchParams {
  if (guards.isSearchParams(obj)) return obj;
  throw new ParseError('Объект не соответствует типу SearchParams');
}

export function parseSubscriptionDetails(
  obj: unknown
): AppTypes.SubscriptionDetails {
  if (guards.isSubscriptionDetails(obj)) return obj;
  throw new ParseError('Объект не соответствует типу SubscriptionDetails');
}

export function parseSubscriptionDetailsArray(
  obj: unknown
): AppTypes.SubscriptionDetails[] {
  if (!Array.isArray(obj)) throw new ParseError('Объект не является массивом');

  return obj
    .map((item: unknown) => {
      if (guards.isSubscriptionDetails(item)) {
        return item;
      }

      return null;
    })
    .filter((item): item is AppTypes.SubscriptionDetails => item !== null); // Отфильтровываем некорректные объекты
}

export function parseSubscription(obj: unknown): AppTypes.Subscription {
  if (guards.isSubscription(obj)) return obj;
  throw new ParseError('Объект не соответствует типу Subscription');
}

export function parseVKData(obj: unknown): AppTypes.VKData {
  if (guards.isVKData(obj)) return obj;
  throw new ParseError('Объект не соответствует типу VKData');
}

export function parseRegistrationData(obj: unknown): AppTypes.RegistrationData {
  if (guards.isRegistrationData(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу RegistrationData');
}

export function parseSignUpSessionForClient(
  obj: unknown
): AppTypes.SignUpSessionForClient {
  if (guards.isSignUpSessionForClient(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу SignUpSessionForClient');
}

export function parseSignUpSessionForRedisIncomingData(
  obj: unknown
): AppTypes.SignUpSessionForRedisIncomingData {
  if (guards.isSignUpSessionForRedisIncomingData(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу SignUpSessionForRedisIncomingData'
  );
}

export function parseSignUpSessionForRedis(
  obj: unknown
): AppTypes.SignUpSessionForRedis {
  if (guards.isSignUpSessionForRedis(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу SignUpSessionForRedis');
}

export const parseEncryptedVkData = (obj: unknown): EncryptedVkData => {
  if (guards.isEncryptedVkData(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу EncryptedVkData');
};

export const parseUserDataForSessionsDB = (
  obj: unknown
): AppTypes.UserDataForSessionsDB => {
  if (guards.isUserDataForSessionsDB(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу UserDataForSessionsDB');
};

export const parseSessionIdAndUserClient = (
  obj: unknown
): AppTypes.SessionIdAndUserClient => {
  if (guards.isSessionIdAndUserClient(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу SessionIdAndUserClient');
};

export const parseSessionRedis = (obj: unknown): AppTypes.SessionRedis => {
  if (guards.isSessionRedis(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу SessionRedis');
};

export const parseSessionClient = (obj: unknown): AppTypes.SessionClient => {
  if (guards.isSessionClient(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу SessionClient');
};

export const parseSessionClientFromUserService = (
  obj: unknown
): AppTypes.SessionClientFromUserService => {
  if (guards.isSessionClientFromUserService(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу SessionClientFromUserService'
  );
};

export const parseConfigUrlData = (obj: unknown): AppTypes.ConfigUrlData => {
  if (guards.isConfigUrlData(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу ConfigUrlData');
};

export const parsePaginationData = (obj: unknown): AppTypes.PaginationData => {
  if (guards.isPaginationData(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу PaginationData');
};

export const parseCreateUserArgs = (obj: unknown): UserTypes.CreateUserArgs => {
  if (guards.isCreateUserArgs(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу CreateUserArgs');
};

export const parseUpdatePersonalDataArgs = (
  obj: unknown
): UserTypes.UpdatePersonalDataArgs => {
  if (guards.isUpdatePersonalDataArgs(obj)) {
    return obj;
  }
  console.error(`Объект не соответствует типу UpdatePersonalDataArgs: `, obj);
  throw new ParseError('Объект не соответствует типу UpdatePersonalDataArgs');
};

export const parseBranchWithParsedCasesArray = (
  obj: unknown
): BranchWithParsedCases[] => {
  if (!obj) return [];
  if (guards.isBranchWithParsedCasesArray(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу BranchWithParsedCases[]');
};

export const parseCases = (obj: unknown): AppTypes.Cases => {
  if (guards.isCases(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу isCases');
};

export const parseBranchForSubscriptionArray = (
  obj: unknown
): BranchForSubscription[] => {
  if (guards.isBranchForSubscriptionArray(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу BranchForSubscription[]');
};

export function parseAllInSubscriptionTableData(
  obj: unknown
): AppTypes.AllInSubscriptionTableData {
  if (guards.isAllInSubscriptionTableData(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу isAllInSubscriptionTableData'
  );
}

export function parseCustomSubscriptionTableData(
  obj: unknown
): AppTypes.CustomSubscriptionTableData {
  if (guards.isCustomSubscriptionTableData(obj)) {
    return obj;
  }
  throw new ParseError(
    'Объект не соответствует типу CustomSubscriptionTableData'
  );
}

export function parseProtectedRoute(value: unknown): AppTypes.ProtectedRoute {
  if (value === 'premium' || value === 'myaccount') {
    return value;
  }
  return undefined;
}

export function parseSignUpRoute(value: unknown): AppTypes.SignUpRoute {
  if (value === 'create-account' || value === 'payment') {
    return value;
  }
  return undefined;
}

export function parseMetadataClient(obj: unknown): AppTypes.MetadataClient {
  if (guards.isMetadataClient(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу MetadataClient');
}

export function parseOpenGraph(obj: unknown): OpenGraph {
  if (guards.isOpenGraph(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу OpenGraph');
}

export function parseOpenGraphDB(obj: unknown): AppTypes.OpenGraphDB {
  if (guards.isOpenGraphDB(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу isOpenGraphDB');
}
export function parseOpenGraphImagesDB(
  obj: unknown
): AppTypes.OpenGraphImageDB[] {
  if (guards.isOpenGraphImageDBArray(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу isOpenGraphDB');
}

export function jsonValueToOpenGraphDB(
  openGraph: Prisma.JsonValue
): AppTypes.OpenGraphDB {
  if (guards.isOpenGraphDB(openGraph)) {
    return openGraph;
  }
  return {} as AppTypes.OpenGraphDB; // Возвращаем пустой объект, если данные невалидные
}
export function jsonValueToOpenGraph(openGraph: Prisma.JsonValue): OpenGraph {
  if (guards.isOpenGraph(openGraph)) {
    return openGraph;
  }
  return {} as OpenGraph; // Возвращаем пустой объект, если данные невалидные
}

export function parseSubscriptionForSchema(
  obj: unknown
): SubscriptionForSchema[] {
  if (!obj) return [] as SubscriptionForSchema[];

  if (guards.isSubscriptionForSchemaArray(obj)) {
    return obj;
  }
  throw new ParseError('Объект не соответствует типу SubscriptionForSchema[]');
}
